import { ref, computed, watch } from 'vue';
import { t } from '@/utils/i18n-helper';
import abonosService from '@/services/perfil/abonos';
import { isPlazoPagable } from '@/composables/front/perfil/plazos';
import { useSearchDebounce } from '@/composables/front/common/useSearchDebounce';
import { useListasAbonados } from './useListasAbonados';

export function useAbonos() {
    const abonos = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const { search, debouncedSearch } = useSearchDebounce({ delay: 600, minChars: 1 });

    const meta = ref({ current_page: 1, last_page: 1, total: 0, per_page: 5 });

    const page    = computed(() => meta.value.current_page);
    const perPage = computed(() => meta.value.per_page);

    let fetchGeneration = 0;

    // ── Listas de abonados filter ──
    const {
        listas,
        listasLoading,
        selectedListaId,
        onListaFilterChange,
    } = useListasAbonados({
        onFilterChange: () => { fetchAbonos(1, perPage.value); },
    });

    const detalleCache = ref(/** @type {Map<number|string, Object>} */ (new Map()));

    const detalleLoadingIds = ref(/** @type {Set<number|string>} */ (new Set()));

    const desmarcarLoadingIds = ref(/** @type {Set<number|string>} */ (new Set()));

    const fetchAbonos = async(newPage = page.value, newPerPage = perPage.value) => {
        const generation = ++fetchGeneration;
        loading.value = true;
        error.value = null;
        try {
            const result = await abonosService.getAbonados({
                page: newPage,
                per_page: newPerPage,
                search: search.value,
                lista_id: selectedListaId.value,
            });

            if (generation !== fetchGeneration) return;

            abonos.value = result.items;
            meta.value = result.meta;
        } catch(err) {
            if (generation !== fetchGeneration) return;

            error.value = err?.message ?? t('perfil.error_cargar_abonos', {}, 'Error al cargar los abonos');
            abonos.value = [];
            meta.value = { current_page: 1, last_page: 1, total: 0, per_page: newPerPage };

            if (process.env.NODE_ENV !== 'production') {
                console.error('Error al obtener abonos:', err);
            }
        } finally {
            if (generation === fetchGeneration) {
                loading.value = false;
            }
        }
    };

    const loadDetalle = async abonadoId => {
        if (detalleCache.value.has(abonadoId)) return;
        if (detalleLoadingIds.value.has(abonadoId)) return;

        const nextLoading = new Set(detalleLoadingIds.value);
        nextLoading.add(abonadoId);
        detalleLoadingIds.value = nextLoading;

        try {
            const detalle = await abonosService.getAbonadoDetalle(abonadoId);
            const nextCache = new Map(detalleCache.value);
            nextCache.set(abonadoId, detalle);
            detalleCache.value = nextCache;
        } catch(err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(`[useAbonos] Error al obtener detalle del abonado ${abonadoId}:`, err);
            }
        } finally {
            const nextLoading2 = new Set(detalleLoadingIds.value);
            nextLoading2.delete(abonadoId);
            detalleLoadingIds.value = nextLoading2;
        }
    };

    const desmarcarRenovacion = async abonadoId => {
        if (desmarcarLoadingIds.value.has(abonadoId)) return;

        const nextLoading = new Set(desmarcarLoadingIds.value);
        nextLoading.add(abonadoId);
        desmarcarLoadingIds.value = nextLoading;

        try {
            await abonosService.desmarcarRenovacionAutomatica(abonadoId);

            const abono = abonos.value.find(a => a.id === abonadoId);
            if (abono) {
                abono.renovacion_automatica = false;
            }
            if (detalleCache.value.has(abonadoId)) {
                const nextCache = new Map(detalleCache.value);
                nextCache.delete(abonadoId);
                detalleCache.value = nextCache;
            }
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(`[useAbonos] Error al desmarcar renovación del abonado ${abonadoId}:`, err);
            }
        } finally {
            const nextLoading2 = new Set(desmarcarLoadingIds.value);
            nextLoading2.delete(abonadoId);
            desmarcarLoadingIds.value = nextLoading2;
        }
    };

    /**
     * Invalida la entrada de caché de un abono y recarga su detalle.
     */
    const invalidateAndReload = async abonadoId => {
        const nextCache = new Map(detalleCache.value);
        nextCache.delete(abonadoId);
        detalleCache.value = nextCache;
        await loadDetalle(abonadoId);
    };

    // ── Plazos selection (simple ref + composite keys, like old branch) ──
    const selectedPlazos = ref({});

    const selectedCount = computed(() => Object.keys(selectedPlazos.value).length);

    const totalSeleccionado = computed(() => {
        let total = 0;
        for (const plazo of Object.values(selectedPlazos.value)) {
            total += plazo.importe_con_gd ?? plazo.importe ?? 0;
        }
        return total;
    });

    const isSelected = (abonoId, plazoId) => {
        return !!selectedPlazos.value[`${abonoId}:${plazoId}`];
    };

    const togglePlazo = (abonoId, plazoId, estadoPlazo) => {
        if (!isPlazoPagable(estadoPlazo)) return;
        const key = `${abonoId}:${plazoId}`;
        const next = { ...selectedPlazos.value };
        if (next[key]) {
            delete next[key];
        } else {
            const detalle = detalleCache.value.get(abonoId);
            const plazo = detalle?.plazos?.find(p => p.id === plazoId);
            if (plazo) {
                next[key] = plazo;
            }
        }
        selectedPlazos.value = next;
    };

    // ── Pago (loading se usa como prop del modal) ──────────────────────
    const pagoLoading = ref(false);

    /** Limpia selección, caché y refresca tras pago desde modal. */
    const onPagoExternoCompletado = () => {
        selectedPlazos.value = {};
        detalleCache.value = new Map();
        fetchAbonos();
    };

    watch(debouncedSearch, () => {
        fetchAbonos(1, perPage.value);
    });

    return {
        abonos,
        loading,
        error,
        search,
        meta,
        page,
        perPage,
        fetchAbonos,
        desmarcarRenovacion,
        detalleCache,
        invalidateAndReload,
        detalleLoadingIds,
        loadDetalle,
        desmarcarLoadingIds,
        togglePlazo,
        isSelected,
        selectedPlazos,
        selectedCount,
        totalSeleccionado,
        pagoLoading,
        onPagoExternoCompletado,
        // listas de abonados filter
        listas,
        listasLoading,
        selectedListaId,
        onListaFilterChange,
    };
}
